import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from "@mui/material/SnackbarContent";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import type { SnackbarMessage } from "../../../store/slices/snackbarSlice";
import { snackbarQueueItemProcessed } from "../../../store/slices/snackbarSlice";

export type { SnackbarMessage };
export type SnackbarVariant = "primary" | "error";

type KeyedSnackbarMessage = SnackbarMessage & {
  id: number;
};

const StyledSnackbarContent = styled(SnackbarContent)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  ...theme.typography.body2,
}));

const MessageContent = styled("span")(() => ({
  display: "flex",
  alignItems: "center",
}));

const MessageIcon = styled("span")(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

function SnackbarQueueBase() {
  const queue = useAppSelector((state) => state.snackbar);
  const dispatch = useAppDispatch();

  const [open, setOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<
    KeyedSnackbarMessage | undefined
  >(undefined);

  const handleClose = React.useCallback(() => setOpen(false), []);
  const handleExited = () => dispatch(snackbarQueueItemProcessed());

  React.useEffect(() => {
    const processQueue = () => {
      if (queue.length > 0) {
        setSnackbarMessage({ ...queue[0], id: new Date().getTime() });
        dispatch(snackbarQueueItemProcessed());
        setOpen(true);
      }
    };

    open ? setOpen(false) : processQueue();
  }, [queue, open, setSnackbarMessage, dispatch, setOpen]);

  if (!snackbarMessage) {
    return null;
  }

  const renderIcon = (snackbarMessage: SnackbarMessage) => {
    switch (snackbarMessage.variant) {
      case "primary":
        return (
          <MessageIcon>
            <CheckCircleOutlineIcon color="primary" />
          </MessageIcon>
        );
      case "error":
        return (
          <MessageIcon>
            <ErrorOutlineIcon color="error" />
          </MessageIcon>
        );
      // no default
    }
  };

  return (
    <Snackbar
      key={snackbarMessage?.id}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      TransitionProps={{
        onExited: handleExited,
      }}
      ContentProps={{
        "aria-describedby": "message-id",
      }}
    >
      <StyledSnackbarContent
        message={
          snackbarMessage && (
            <MessageContent>
              {renderIcon(snackbarMessage)}
              {snackbarMessage.message}
            </MessageContent>
          )
        }
        action={
          snackbarMessage?.link && (
            <Link to={snackbarMessage.link.path}>
              <Button color="primary">{snackbarMessage.link.text}</Button>
            </Link>
          )
        }
      />
    </Snackbar>
  );
}

const SnackbarQueue = SnackbarQueueBase;

export default SnackbarQueue;
